<template>
  <v-dialog
    :value="dialog"
    @input="(v) => $emit('update:dialog', v)"
    max-width="680px"
    :fullscreen="$vuetify.breakpoint.xs"
    content-class="modern-dialog"
    transition="dialog-bottom-transition"
  >
    <v-card v-if="need" class="need-dialog-card">
      <!-- Header -->
      <div class="need-header" :style="{ background: categoryGradient }">
        <v-btn icon dark @click="closeDialog" class="close-btn">
          <v-icon>close</v-icon>
        </v-btn>
        <div class="need-header-row">
          <v-avatar color="white" size="50" class="need-avatar">
            <v-icon :color="categoryColor">{{ categoryIcon }}</v-icon>
          </v-avatar>
          <div class="need-header-text">
            <h2 class="need-title">{{ need.label }}</h2>
            <p class="need-meta">
              {{ need.client_count }} client(s) &middot; {{ need.intent_count }} intention(s)
              <template v-if="need.avg_amount">&middot; avg. goal {{ formatAmount(need.avg_amount) }}</template>
            </p>
          </div>
        </div>
      </div>

      <div class="need-body">
        <!-- Stats row -->
        <div class="need-stats">
          <div class="need-stat">
            <span class="ns-value">{{ need.client_count }}</span>
            <span class="ns-label">Clients</span>
          </div>
          <div class="need-stat">
            <span class="ns-value">{{ need.intent_count }}</span>
            <span class="ns-label">Intentions</span>
          </div>
          <div class="need-stat">
            <span class="ns-value">{{ formatAmountShort(need.avg_amount) }}</span>
            <span class="ns-label">Avg. goal</span>
          </div>
          <div class="need-stat">
            <span class="ns-value">{{ formatAmountShort(need.total_amount) }}</span>
            <span class="ns-label">Total target</span>
          </div>
        </div>

        <!-- Urgency distribution -->
        <div v-if="urgencyItems.length" class="need-block">
          <h4 class="need-block-title">Urgency</h4>
          <div class="tag-row">
            <span
              v-for="(u, i) in urgencyItems"
              :key="'u' + i"
              class="urgency-chip"
              :class="'ure-' + u.key"
            >
              <v-icon x-small>{{ getUrgencyIcon(u.key) }}</v-icon>
              {{ u.key.toUpperCase() }} ({{ u.value }})
            </span>
          </div>
        </div>

        <!-- Dominant keywords -->
        <div v-if="need.top_keywords.length" class="need-block">
          <div class="need-block-head clickable" @click="keywordsOpen = !keywordsOpen">
            <h4 class="need-block-title">
              <v-icon x-small class="toggle-chevron" :style="{ transform: keywordsOpen ? 'rotate(180deg)' : 'none' }">expand_more</v-icon>
              Main keywords
            </h4>
            <span v-if="!keywordsOpen" class="collapse-count">
              {{ need.top_keywords.length }} keyword{{ need.top_keywords.length > 1 ? 's' : '' }}
            </span>
          </div>
          <transition name="collapse">
            <div v-show="keywordsOpen" class="tag-row">
              <span v-for="(kw, i) in need.top_keywords" :key="'k' + i" class="kw-chip">{{ kw }}</span>
            </div>
          </transition>
        </div>

        <!-- Subcases -->
        <div v-if="need.subcases.length" class="need-block">
          <div class="need-block-head clickable" @click="variationsOpen = !variationsOpen">
            <h4 class="need-block-title">
              <v-icon x-small class="toggle-chevron" :style="{ transform: variationsOpen ? 'rotate(180deg)' : 'none' }">expand_more</v-icon>
              Variations of this need
            </h4>
            <span v-if="!variationsOpen" class="collapse-count">
              {{ need.subcases.length }} variation{{ need.subcases.length > 1 ? 's' : '' }}
            </span>
          </div>
          <transition name="collapse">
            <div v-show="variationsOpen" class="subcase-row">
              <div v-for="(sc, i) in need.subcases" :key="'sc' + i" class="subcase-chip">
                <span class="subcase-kw">{{ sc.keyword }}</span>
                <span class="subcase-count">{{ sc.client_count }} client(s)</span>
              </div>
            </div>
          </transition>
        </div>

        <!-- Sample intents -->
        <div v-if="need.sample_intents.length" class="need-block">
          <h4 class="need-block-title">Most recent intentions</h4>
          <div v-for="(s, i) in need.sample_intents" :key="'s' + i" class="sample-item">
            <span class="sample-name">{{ s.username }}</span>
            <span class="sample-text">"{{ truncate(s.text, 90) }}"</span>
          </div>
        </div>

        <!-- Clients -->
        <div class="need-block">
          <div class="need-block-head">
            <h4 class="need-block-title">Clients in this case ({{ filteredClients.length }}) &mdash; {{ selectedClients.length }} selected</h4>
            <div class="need-block-head-actions">
              <v-text-field
                v-model="clientSearch"
                prepend-inner-icon="search"
                placeholder="Search a client..."
                solo
                flat
                hide-details
                dense
                class="client-search"
                background-color="rgba(0,150,136,0.06)"
              ></v-text-field>
              <v-btn small text color="teal" @click="toggleAllClients">
                {{ allSelected ? 'Clear all' : 'Select all' }}
              </v-btn>
            </div>
          </div>
          <div class="clients-list">
            <div
              v-for="c in filteredClients"
              :key="c.user_id"
              class="client-row"
              :class="{ active: isClientSelected(c) }"
              @click="toggleClient(c)"
            >
              <div class="client-check">
                <v-icon :color="isClientSelected(c) ? 'teal' : 'grey lighten-1'">
                  {{ isClientSelected(c) ? 'check_circle' : 'radio_button_unchecked' }}
                </v-icon>
              </div>
              <div class="client-avatar" :style="{ background: categoryGradient }">
                <span>{{ getInitials(c.username) }}</span>
              </div>
              <div class="client-info">
                <span class="client-name">{{ c.username }}</span>
                <span class="client-sub">
                  {{ c.intent_count }} intention(s) &middot; active {{ formatDate(c.last_activity) }}
                </span>
                <div v-if="c.top_keywords.length" class="client-kw">
                  <span v-for="(kw, ki) in c.top_keywords.slice(0, 4)" :key="'ck' + ki" class="mini-kw">{{ kw }}</span>
                </div>
              </div>
            </div>
            <div v-if="filteredClients.length === 0" class="list-empty">No clients found</div>
          </div>
        </div>

        <!-- Suggested products -->
        <div v-if="need.suggested_products.length" class="need-block">
          <div class="need-block-head">
            <h4 class="need-block-title">Recommended products</h4>
            <v-btn small outlined color="teal" @click="openProductPicker()">
              <v-icon small left>add</v-icon>
              Choose other
            </v-btn>
          </div>
          <div class="products-strip">
            <div
              v-for="p in need.suggested_products"
              :key="p.id"
              class="sug-product"
              :class="{ selected: isProductSelected(p) }"
              @click="toggleProduct(p)"
            >
              <div class="sug-check" :class="{ on: isProductSelected(p) }">
                <v-icon x-small>{{ isProductSelected(p) ? 'check' : 'add' }}</v-icon>
              </div>
              <div class="sug-img">
                <img v-if="p.image" :src="p.image" :alt="p.name">
                <v-icon v-else size="28">shopping_bag</v-icon>
              </div>
              <div class="sug-info">
                <span class="sug-name">{{ truncate(p.name, 34) }}</span>
                <span class="sug-price">{{ formatAmount(p.price) }}</span>
                <span v-if="p.category" class="sug-cat">{{ truncate(p.category, 18) }}</span>
              </div>
            </div>
            <div class="sug-product sug-add" @click="openProductPicker()">
              <div class="sug-img">
                <v-icon size="30" color="teal">tune</v-icon>
              </div>
              <div class="sug-info">
                <span class="sug-name">Browse all products</span>
                <span class="sug-cat">Pick any marketplace item</span>
              </div>
            </div>
          </div>
          <p v-if="selectedProducts.length" class="product-attached">
            <v-icon x-small color="teal">check_circle</v-icon>
            {{ selectedProducts.length }} product(s) attached to your message.
          </p>
        </div>
      </div>

      <!-- Footer CTA: open the chat popup -->
      <div class="need-footer">
        <v-btn
          color="teal"
          dark
          depressed
          large
          block
          class="cta-btn"
          @click="openChat()"
          :disabled="!need.clients.length"
        >
          <v-icon left>forum</v-icon>
          Message {{ selectedClients.length || 'a' }} client{{ selectedClients.length === 1 ? '' : 's' }}
          <span v-if="selectedClients.length" class="cta-target">({{ selectedClients.length }} recepient(s))</span>
        </v-btn>
      </div>
    </v-card>

    <!-- ── CHAT POPUP (dialog of dialog, like WhatsApp Share) ── -->
    <v-dialog
      v-model="chatDialog"
      max-width="580px"
      :fullscreen="$vuetify.breakpoint.xs"
      content-class="modern-dialog chat-popup"
      :retain-focus="false"
      transition="dialog-bottom-transition"
    >
      <v-card class="chat-popup-card">
        <!-- Head -->
        <div class="chat-head">
          <div class="chat-head-brand">
            <div class="chat-head-icon"><v-icon color="white">chat</v-icon></div>
            <div>
              <div class="chat-head-title">Message client</div>
              <div class="chat-head-sub">Sent via PettyCash messaging</div>
            </div>
          </div>
          <v-btn icon dark @click="chatDialog = false"><v-icon>close</v-icon></v-btn>
        </div>

        <div class="chat-body">
          <!-- Recipients -->
          <div v-if="chatClients.length" class="recipients-area">
            <div class="recipients-label">
              <v-icon x-small>people</v-icon>
              Recipients ({{ chatClients.length }})
            </div>
            <div class="recipients-chips">
              <span v-for="c in chatClients" :key="c.user_id" class="recipient-chip">
                <span class="rc-avatar" :style="{ background: categoryGradient }">{{ getInitials(c.username) }}</span>
                <span class="rc-name">{{ c.username }}</span>
                <span class="rc-remove" @click="removeRecipient(c)" title="Remove recipient">&times;</span>
              </span>
              <v-menu offset-y left :nudge-width="230">
                <template v-slot:activator="{ on }">
                  <v-btn small outlined color="white" v-on="on" class="add-recipient-btn">
                    <v-icon x-small>add</v-icon> Add
                  </v-btn>
                </template>
                <v-list dense max-height="220" class="switch-list">
                  <v-list-item v-for="c in clientsNotRecipients" :key="c.user_id" @click="addRecipient(c)">
                    <v-list-item-icon><v-icon small>person</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ c.username }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-if="clientsNotRecipients.length === 0" disabled>
                    <v-list-item-content>
                      <v-list-item-title>All clients added</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </div>

          <!-- Templates -->
          <div class="chat-templates">
            <span class="chat-templates-label"><v-icon x-small>bolt</v-icon> Quick templates</span>
            <div class="chat-templates-row">
              <span
                v-for="(p, i) in presets"
                :key="'p' + i"
                class="preset-chip"
                @click="draft = p.text"
              >{{ p.short }}</span>
            </div>
          </div>

          <!-- Attached products -->
          <div class="attached-products">
            <div class="attached-label">
              <v-icon x-small>shopping_bag</v-icon>
              Products ({{ selectedProducts.length }})
            </div>
            <div v-if="selectedProducts.length" class="ap-chips">
              <span v-for="p in selectedProducts" :key="p.id" class="ap-chip">
                <img v-if="p.image" :src="p.image" :alt="p.name">
                <v-icon v-else x-small>shopping_bag</v-icon>
                <span class="ap-chip-name">{{ truncate(p.name, 22) }}</span>
                <span class="ap-chip-price">{{ formatAmount(p.price) }}</span>
                <v-icon x-small class="ap-view" @click.stop="viewInMarketplace(p)" title="View in marketplace">open_in_new</v-icon>
                <span class="rc-remove" @click="detachProduct(p)" title="Remove product">&times;</span>
              </span>
              <v-btn small outlined color="teal" class="add-product-btn" @click="openProductPicker()">
                <v-icon x-small left>add</v-icon>
                Add product
              </v-btn>
              <span v-if="sendingProductImage" class="ap-status">
                <v-progress-circular indeterminate size="12" color="teal"></v-progress-circular>
                loading image to send…
              </span>
            </div>
            <div v-else class="ap-empty">
              <v-btn small outlined color="teal" @click="openProductPicker()">
                <v-icon x-small left>add</v-icon> Attach a product
              </v-btn>
            </div>
          </div>

          <div v-if="selectedProducts.length" class="view-marketplace-row">
            <v-btn small text color="teal" class="text-none font-weight-bold" @click="viewAllInMarketplace">
              <v-icon x-small left>storefront</v-icon>
              View in the marketplace
            </v-btn>
          </div>

          <!-- Notify channels -->
          <div class="notify-channels">
            <div class="notify-label">
              <v-icon x-small>notification_important</v-icon>
              Notify clients via
            </div>
            <div class="notify-row">
              <v-chip
                small
                class="notify-chip"
                :class="{ active: channels.whatsapp }"
                @click="channels.whatsapp = !channels.whatsapp"
              >
                <v-icon x-small>{{ channels.whatsapp ? 'check_circle' : 'radio_button_unchecked' }}</v-icon>
                WhatsApp
              </v-chip>
              <v-chip
                small
                class="notify-chip"
                :class="{ active: channels.webpush }"
                @click="channels.webpush = !channels.webpush"
              >
                <v-icon x-small>{{ channels.webpush ? 'check_circle' : 'radio_button_unchecked' }}</v-icon>
                WebPush
              </v-chip>
              <v-chip
                small
                class="notify-chip"
                :class="{ active: channels.email }"
                @click="channels.email = !channels.email"
              >
                <v-icon x-small>{{ channels.email ? 'check_circle' : 'radio_button_unchecked' }}</v-icon>
                Email
              </v-chip>
            </div>
            <span class="notify-hint">Clients can tap the notification to open the chat.</span>
          </div>

          <!-- Composer -->
          <div class="form-group">
            <label class="chat-label">Message</label>
            <v-textarea
              v-model="draft"
              rows="4"
              auto-grow
              solo
              flat
              hide-details
              placeholder="Write a message… or use a quick template"
              class="chat-composer-input"
              background-color="rgba(0,0,0,0.04)"
              @keydown.enter.exact.prevent="sendChat"
            ></v-textarea>
            <div class="chat-textarea-foot">
              <input
                ref="fileInput"
                type="file"
                accept="image/*,video/*"
                style="display:none"
                @change="onFileSelected"
              >
              <v-btn icon small @click="$refs.fileInput.click()" title="Attach an image or video">
                <v-icon>attach_file</v-icon>
              </v-btn>
              <span v-if="selectedFile" class="file-chip">
                <v-icon x-small>file_present</v-icon>
                {{ truncate(selectedFile.name, 22) }}
                <span class="file-remove" @click="removeFile">&times;</span>
              </span>
              <v-spacer></v-spacer>
              <span class="char-count" :class="{ 'char-warn': charCount > 900 }">{{ charCount }}/1000</span>
            </div>
          </div>

          <!-- Preview -->
          <div class="chat-preview">
            <div class="chat-preview-head">
              <v-icon small color="white">message</v-icon>
              Preview for {{ chatClients.length === 1 ? chatClients[0].username : (chatClients.length + ' clients') }}
            </div>
            <div class="chat-preview-frame">
              <div v-if="previewAttachment" class="chat-preview-attach">
                <div class="chat-preview-attach-thumb">
                  <img v-if="previewAttachment.image" :src="previewAttachment.image" :alt="previewAttachment.name">
                  <v-icon v-else size="18">attach_file</v-icon>
                </div>
                <span class="chat-preview-attach-name">{{ previewAttachment.name }}</span>
                <v-icon x-small color="rgba(0,0,0,0.45)">file_present</v-icon>
              </div>
              <div class="chat-preview-bubble">{{ finalMessage || 'Your message preview will appear here.' }}</div>
              <span class="chat-preview-tick">PettyCash ✓✓</span>
            </div>
          </div>
        </div>

        <!-- Foot -->
        <div class="chat-foot">
          <v-btn text @click="chatDialog = false" class="phost-btn">Cancel</v-btn>
          <v-btn
            color="teal"
            dark
            depressed
            class="send-btn"
            :loading="sending"
            :disabled="!chatClients.length || !finalMessage"
            @click="sendChat"
          >
            <v-icon small left>send</v-icon>
            Send
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ── PRODUCT PICKER (choose other marketplace products) ── -->
    <v-dialog
      v-model="productDialog"
      max-width="640px"
      :fullscreen="$vuetify.breakpoint.xs"
      content-class="modern-dialog product-popup"
      :retain-focus="false"
      transition="dialog-bottom-transition"
    >
      <v-card class="product-popup-card">
        <div class="product-popup-head" :style="{ background: categoryGradient }">
          <div class="product-popup-brand">
            <div class="pp-icon"><v-icon color="white">shopping_bag</v-icon></div>
            <div>
              <div class="pp-title">Choose a marketplace product</div>
              <div class="pp-sub">Pick any product to propose in your message</div>
            </div>
          </div>
          <v-btn icon dark @click="productDialog = false"><v-icon>close</v-icon></v-btn>
        </div>

        <div class="product-popup-body">
          <div class="pp-controls">
            <v-text-field
              v-model="browseSearch"
              prepend-inner-icon="search"
              placeholder="Search products…"
              solo
              flat
              hide-details
              dense
              class="pp-search"
              background-color="rgba(0,0,0,0.05)"
              @input="loadBrowse"
            ></v-text-field>
            <v-text-field
              v-model="browseMaxPrice"
              placeholder="Max price (FCFA)"
              solo
              flat
              hide-details
              dense
              type="number"
              class="pp-price"
              background-color="rgba(0,0,0,0.05)"
              @change="loadBrowse"
            ></v-text-field>
          </div>

          <div class="pp-cats">
            <span
              class="pp-cat-chip"
              :class="{ active: !browseCategory }"
              @click="browseCategory = ''; loadBrowse()"
            >All</span>
            <span
              v-for="c in browseCategories"
              :key="c"
              class="pp-cat-chip"
              :class="{ active: browseCategory === c }"
              @click="browseCategory = c; loadBrowse()"
            >{{ c }}</span>
          </div>

          <v-divider class="pp-divider"></v-divider>

          <div v-if="browseLoading" class="pp-loading">
            <v-progress-circular indeterminate size="26" color="teal"></v-progress-circular>
          </div>

          <div v-else-if="browseProducts.length === 0" class="pp-empty">
            <v-icon size="52" color="grey lighten-1">inventory_2</v-icon>
            <p>No products found</p>
          </div>

          <div v-else class="pp-list">
            <div
              v-for="p in browseProducts"
              :key="p.id"
              class="pp-item"
              :class="{ attached: isProductSelected(p) }"
              @click="toggleProduct(p)"
            >
              <div class="pp-item-img">
                <img v-if="p.image" :src="p.image" :alt="p.name">
                <v-icon v-else>shopping_bag</v-icon>
              </div>
              <div class="pp-item-info">
                <span class="pp-item-name">{{ truncate(p.name, 48) }}</span>
                <span class="pp-item-cat">{{ p.category || 'Marketplace' }} <span :class="p.in_stock ? 'in-stock' : 'out-stock'">{{ p.in_stock ? '· in stock' : '· out of stock' }}</span></span>
                <span class="pp-item-price">{{ formatAmount(p.price) }}</span>
              </div>
              <v-icon small :color="isProductSelected(p) ? 'teal' : 'grey'">{{ isProductSelected(p) ? 'check_circle' : 'add_circle_outline' }}</v-icon>
            </div>
          </div>
        </div>

        <div class="product-popup-foot">
          <v-btn text @click="productDialog = false" class="phost-btn">Cancel</v-btn>
          <span class="pp-foot-count">{{ selectedProducts.length }} selected</span>
          <v-btn
            color="teal"
            dark
            depressed
            @click="donePicking()"
          >
            <v-icon small left>check</v-icon>
            Done
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import axios from 'axios'

const CHAT_LINK = 'https://myapp.pettycashglobal.com/?open_chat=true'

export default {
  name: 'RecurringNeedsDialog',
  props: {
    dialog: { type: Boolean, default: false },
    need: { type: Object, default: null }
  },
  data() {
    return {
      selectedClientIds: [],
      keywordsOpen: false,
      variationsOpen: false,
      clientSearch: '',
      chatDialog: false,
      chatClients: [],
      draft: '',
      selectedFile: null,
      sending: false,
      sendingProductImage: false,
      selectedProducts: [],
      channels: { whatsapp: false, webpush: true, email: false },
      productDialog: false,
      browseLoading: false,
      browseSearch: '',
      browseMaxPrice: '',
      browseCategory: '',
      browseCategories: [],
      browseProducts: []
    }
  },
  computed: {
    category() {
      return (this.need && this.need.category) || 'other'
    },
    categoryGradient() {
      const gradients = {
        'education': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'business': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        'health': 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        'property': 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
        'technology': 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        'lifestyle': 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
        'emergency': 'linear-gradient(135deg, #ffa751 0%, #ffe259 100%)',
        'investment': 'linear-gradient(135deg, #21d4fd 0%, #b721ff 100%)',
        'travel': 'linear-gradient(135deg, #08aeea 0%, #2af598 100%)',
        'transport': 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
        'other': 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)'
      }
      return gradients[this.category] || gradients['other']
    },
    categoryColor() {
      const colors = {
        'education': '#667eea',
        'business': '#f5576c',
        'health': '#f5576c',
        'property': '#30cfd0',
        'technology': '#38b2ac',
        'lifestyle': '#ff9a9e',
        'emergency': '#ffa751',
        'investment': '#b721ff',
        'travel': '#08aeea',
        'transport': '#fda085',
        'other': '#8ec5fc'
      }
      return colors[this.category] || '#8ec5fc'
    },
    categoryIcon() {
      const icons = {
        'education': 'school',
        'business': 'business_center',
        'health': 'local_hospital',
        'property': 'home_work',
        'technology': 'devices',
        'lifestyle': 'spa',
        'emergency': 'emergency',
        'investment': 'trending_up',
        'travel': 'flight',
        'transport': 'directions_car',
        'other': 'category'
      }
      return icons[this.category] || 'category'
    },
    allClients() {
      return (this.need && this.need.clients) || []
    },
    selectedClients() {
      return this.allClients.filter(c => this.selectedClientIds.includes(c.user_id))
    },
    allSelected() {
      return this.allClients.length > 0 && this.selectedClientIds.length === this.allClients.length
    },
    clientsNotRecipients() {
      return this.allClients.filter(c => !this.chatClients.some(r => r.user_id === c.user_id))
    },
    filteredClients() {
      const q = this.clientSearch.trim().toLowerCase()
      if (!q) return this.allClients
      return this.allClients.filter(c => (c.username || '').toLowerCase().includes(q))
    },
    urgencyItems() {
      const dist = (this.need && this.need.urgency_distribution) || {}
      return Object.keys(dist).map(key => ({ key, value: dist[key] }))
    },
    presets() {
      const recipients = this.chatClients.length ? this.chatClients : this.selectedClients
      const single = recipients.length === 1 ? recipients[0] : null
      const greeting = this.timeGreeting()
      const address = single
        ? greeting + (this.displayName(single) ? ', ' + this.displayName(single) : '') + '.'
        : (recipients.length > 1 ? 'Hello everyone,' : greeting + '.')
      const subject = this.dominantSubject()
      const urgency = this.dominantUrgency()
      const avg = (this.need && this.need.avg_amount) || 0
      const products = this.selectedProducts
      const list = []

      // 1) Personalized opener — the smart default (quotes the client's own words when alone)
      let opener = address + '\n\n'
      opener += 'I hope you are having a good day. We noticed you have been planning a project around "' + subject + '"'
      const quote = single ? this.recipientQuote(single) : ''
      if (quote) opener += ' — you recently shared: "' + this.truncate(quote, 64) + '"'
      if (urgency === 'critical') opener += ' and you marked it as urgent'
      else if (urgency === 'high') opener += ' and it is clearly a priority for you right now'
      opener += ' — a goal we love helping clients bring to life.\n\n'
      opener += 'With a savings plan structured around your target amount and timeline, a goal like this can feel far more reachable.\n\n'
      opener += 'Would you like us to prepare a tailored savings path for you? Just reply here — it only takes a minute.\n\n'
      opener += '— Your PettyCash advisor'
      list.push({ short: 'Personal', text: opener })

      // 2) Goal-amount aware
      if (avg > 0) {
        let amountText = address + '\n\n'
        amountText += 'Did you know goals like yours around "' + subject + '" typically target about ' + this.formatAmount(avg) + ' on PettyCash? With a comfortable weekly contribution, that becomes reachable in just a few months.\n\n'
        amountText += 'We can map the numbers for you and set a realistic target together. Shall we?\n\n'
        amountText += '— Your PettyCash advisor'
        list.push({ short: 'With amount', text: amountText })
      }

      // 3) Product-aware (product details are appended to the message automatically)
      if (products.length) {
        let productText = address + '\n\n'
        productText += 'To help you move forward on your "' + subject + '" goal, I have shortlisted a few products below that clients with a similar budget often pick.\n\n'
        productText += 'Take a look — if any of them catches your eye, just let me know and I will explain how it can fit into your savings plan.\n\n'
        productText += '— Your PettyCash advisor'
        list.push({ short: 'With product', text: productText })
      }

      // 4) Priority nudge (only when urgency is critical/high)
      if (urgency === 'critical' || urgency === 'high') {
        let priorityText = address + '\n\n'
        priorityText += 'I see your "' + subject + '" goal is marked as urgent — thank you for sharing that with us. We want to make sure you have a clear, realistic plan as soon as possible.\n\n'
        priorityText += 'Could we set up a tailored savings plan for you in the coming days? A quick reply is all we need to get started.\n\n'
        priorityText += '— Your PettyCash advisor'
        list.push({ short: 'Priority', text: priorityText })
      }

      // 5) Follow-up / check-in
      let followText = address + '\n\n'
      followText += 'Just a quick check-in on your "' + subject + '" project — how is everything going?\n\n'
      followText += 'If you need help structuring your savings, adjusting your target, or simply a second opinion, I am here. Feel free to reply whenever it suits you.\n\n'
      followText += '— Your PettyCash advisor'
      list.push({ short: 'Follow-up', text: followText })

      return list
    },
    finalMessage() {
      const parts = []
      if (this.selectedProducts.length) {
        this.selectedProducts.forEach(p => {
          parts.push('I would like to propose the product "' + p.name + '" at ' + this.formatAmount(p.price) + (p.category ? ' (' + p.category + ')' : '') + '.')
        })
      }
      if (this.draft && this.draft.trim()) parts.push(this.draft.trim())
      return parts.join('\n\n')
    },
    charCount() {
      return this.finalMessage.length
    },
    previewAttachment() {
      if (this.selectedFile) {
        return { name: 'Image/Video · ' + this.truncate(this.selectedFile.name, 22), image: null }
      }
      if (this.selectedProducts.length === 1 && this.selectedProducts[0].image) {
        const p = this.selectedProducts[0]
        return { name: 'Product image · ' + this.truncate(p.name, 22), image: p.image }
      }
      return null
    }
  },
  watch: {
    dialog(open) {
      if (open) this.reset()
    },
    need() {
      this.reset()
    }
  },
  methods: {
reset() {
      this.selectedClientIds = []
      this.keywordsOpen = false
      this.variationsOpen = false
      this.chatDialog = false
      this.clientSearch = ''
      this.draft = ''
      this.selectedFile = null
      this.selectedProducts = []
      this.channels = { whatsapp: false, webpush: true, email: false }
      this.productDialog = false
      this.browseSearch = ''
      this.browseMaxPrice = ''
      this.browseCategory = ''
      this.browseProducts = []
      if (this.need && this.need.clients && this.need.clients.length) {
        this.selectedClientIds = [this.need.clients[0].user_id]
      }
    },
    closeDialog() {
      this.$emit('update:dialog', false)
    },
    isClientSelected(c) {
      return this.selectedClientIds.includes(c.user_id)
    },
    toggleClient(c) {
      const i = this.selectedClientIds.indexOf(c.user_id)
      if (i === -1) this.selectedClientIds.push(c.user_id)
      else this.selectedClientIds.splice(i, 1)
    },
    toggleAllClients() {
      if (this.allSelected) this.selectedClientIds = []
      else this.selectedClientIds = this.allClients.map(c => c.user_id)
    },
    openChat(priority) {
      let recipients = []
      if (Array.isArray(priority) && priority.length) recipients = priority
      else if (this.selectedClients.length) recipients = this.selectedClients
      else if (this.allClients.length) recipients = [this.allClients[0]]
      if (!recipients.length) return
      this.chatClients = recipients
      this.chatDialog = true
      this.$nextTick(() => {
        if (this.presets.length) this.draft = this.presets[0].text
      })
    },
    addRecipient(client) {
      if (!this.chatClients.some(r => r.user_id === client.user_id)) this.chatClients.push(client)
    },
    removeRecipient(client) {
      const i = this.chatClients.indexOf(client)
      if (i !== -1) this.chatClients.splice(i, 1)
    },
    // ── Products ──
    async openProductPicker() {
      this.productDialog = true
      if (this.browseCategories.length === 0) {
        try {
          const res = await axios.get('/api/v1/manager/market_place/categories/')
          this.browseCategories = (res.data || [])
            .map(c => (typeof c === 'string' ? c : c.name))
            .filter(Boolean)
        } catch (e) {
          console.error('Error loading categories:', e)
        }
      }
      this.loadBrowse()
    },
    async loadBrowse() {
      this.browseLoading = true
      try {
        const params = new URLSearchParams()
        if (this.browseSearch.trim()) params.append('search', this.browseSearch.trim())
        if (this.browseCategory) params.append('category', this.browseCategory)
        if (this.browseMaxPrice) params.append('max_price', this.browseMaxPrice)
        const res = await axios.get('/api/v1/manager/market_place/products/browse/?' + params.toString())
        this.browseProducts = res.data.products || []
      } catch (error) {
        console.error('Error browsing products:', error)
        this.snack('Failed to load products', 'red darken-4')
      } finally {
        this.browseLoading = false
      }
    },
    isProductSelected(p) {
      return this.selectedProducts.some(x => x.id === p.id)
    },
    toggleProduct(p) {
      const i = this.selectedProducts.findIndex(x => x.id === p.id)
      if (i === -1) this.selectedProducts.push(p)
      else this.selectedProducts.splice(i, 1)
    },
    donePicking() {
      this.productDialog = false
      if (!this.chatClients.length && this.selectedClients.length) this.chatClients = this.selectedClients
    },
    detachProduct(p) {
      const i = this.selectedProducts.findIndex(x => x.id === p.id)
      if (i !== -1) this.selectedProducts.splice(i, 1)
    },
    viewInMarketplace(p) {
      const ids = this.selectedProducts.map(x => x.id).join(',')
      this.$router.push('/market_place/products/showcase?highlight=' + p.id + (this.selectedProducts.length > 1 ? '&ids=' + ids : ''))
    },
    viewAllInMarketplace() {
      if (!this.selectedProducts.length) return
      const ids = this.selectedProducts.map(p => p.id).join(',')
      this.$router.push('/market_place/products/showcase?ids=' + ids + '&highlight=' + this.selectedProducts[0].id)
    },
    // ── Composer helpers ──
    onFileSelected(event) {
      const file = event.target.files && event.target.files[0]
      if (file) this.selectedFile = file
    },
    removeFile() {
      this.selectedFile = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    getFileType(file) {
      if (file.type.startsWith('image/')) return 'image'
      if (file.type.startsWith('video/')) return 'video'
      return 'file'
    },
    async sendChat() {
      const msg = this.finalMessage
      const attachment = await this.prepareAttachment()
      const hasText = !!msg
      if ((!hasText && !attachment) || this.sending) return
      const recipients = this.chatClients.slice()
      if (!recipients.length) return
      this.sending = true
      try {
        const useWhatsapp = this.channels.whatsapp
        const useWebpush = this.channels.webpush
        const useEmail = this.channels.email
        const productsPayload = this.selectedProducts.map(p => ({
          id: p.id,
          name: p.name,
          price: p.price,
          image: p.image || p.thumbnail || '',
          category: p.category || ''
        }))
        const waLinks = []
        const chanErrorDetails = []
        let sent = 0
        for (const client of recipients) {
          const formData = new FormData()
          formData.append('id_client', client.user_id)
          formData.append('message', msg)
          formData.append('message_type', attachment ? attachment.type : 'text')
          formData.append('suppress_webpush', '1')
          if (productsPayload.length) formData.append('products', JSON.stringify(productsPayload))
          if (attachment) formData.append('file', attachment.file)
          await axios.post('/api/v1/manager/save_chat/', formData)
          // Chaque canal est indépendant — un échec ne bloque pas les autres
          if (useWhatsapp) {
            try {
              const waMsg = msg ? msg + '\n\nOpen the chat: ' + CHAT_LINK : 'You received a message'
              const res = await axios.post('/api/v1/manager/partners/' + client.user_id + '/send-whatsapp/', { message: waMsg })
              if (res.data && res.data.whatsapp_url) waLinks.push(res.data.whatsapp_url)
              else chanErrorDetails.push('WhatsApp (' + client.username + '): ' + (res.data.message || 'no phone number'))
            } catch (e) { chanErrorDetails.push('WhatsApp (' + client.username + '): ' + ((e.response && e.response.data && e.response.data.error) || e.message)) }
          }
          if (useWebpush) {
            try {
              const res = await axios.post('/api/v1/manager/partners/' + client.user_id + '/send-webpush/', {
                title: 'New message from PettyCash',
                message: msg || 'You received a message'
              })
              if (res.data && res.data.subscriptions === 0) chanErrorDetails.push('WebPush (' + client.username + '): not subscribed')
            } catch (e) { chanErrorDetails.push('WebPush (' + client.username + '): ' + ((e.response && e.response.data && e.response.data.error) || e.message)) }
          }
          if (useEmail) {
            try {
              await axios.post('/api/v1/manager/partners/' + client.user_id + '/send-email/', {
                subject: 'New message from PettyCash',
                message: msg || 'You received a message'
              })
            } catch (e) { chanErrorDetails.push('Email (' + client.username + '): ' + ((e.response && e.response.data && e.response.data.error) || e.message)) }
          }
          sent++
        }
        if (waLinks.length) {
          waLinks.forEach((url, i) => {
            setTimeout(() => window.open(url, '_blank'), i * 350)
          })
        }
        this.draft = ''
        this.removeFile()
        if (chanErrorDetails.length) console.warn('Channel errors:', chanErrorDetails)
        let snackMsg = 'Message sent to ' + sent + ' client' + (sent === 1 ? '' : 's')
        if (chanErrorDetails.length) snackMsg += ' · ' + chanErrorDetails.length + ' channel error(s)'
        else if (waLinks.length) snackMsg += ' · WhatsApp opened'
        this.snack(snackMsg, chanErrorDetails.length ? 'orange darken-2' : 'teal darken-1')
        this.chatDialog = false
      } catch (error) {
        console.error('Send error:', error)
        this.snack('Failed to send the message', 'red darken-4')
      } finally {
        this.sending = false
      }
    },
    async prepareAttachment() {
      if (this.selectedFile) {
        return { file: this.selectedFile, type: this.getFileType(this.selectedFile) }
      }
      if (this.selectedProducts.length === 1 && this.selectedProducts[0].image) {
        this.sendingProductImage = true
        try {
          const product = this.selectedProducts[0]
          const resp = await axios.get(product.image, { responseType: 'blob' })
          const blob = resp.data
          const rawUrl = String(product.image).split('?')[0]
          const name = decodeURIComponent(rawUrl.split('/').pop() || 'product-image.jpg')
          const file = new File([blob], name, { type: blob.type || 'image/jpeg' })
          return { file, type: 'image' }
        } catch (error) {
          console.warn('Could not download product image, sending as text:', error)
          return null
        } finally {
          this.sendingProductImage = false
        }
      }
      return null
    },
    snack(message, color) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', color || 'teal darken-1')
      this.$store.commit('activateSnackBar', true)
    },
    getUrgencyIcon(urgency) {
      const icons = {
        critical: 'priority_high',
        high: 'arrow_upward',
        medium: 'remove',
        low: 'arrow_downward'
      }
      return icons[urgency] || 'remove'
    },
    // ── Smart preset helpers ──
    timeGreeting() {
      const h = new Date().getHours()
      if (h < 12) return 'Good morning'
      if (h < 18) return 'Good afternoon'
      return 'Good evening'
    },
    displayName(client) {
      if (!client) return ''
      const raw = String(client.username || client.fullnames || '').trim()
      if (!raw) return ''
      let name = raw
      if (name.includes('@')) name = name.split('@')[0]
      name = name.replace(/[._-]+/g, ' ').replace(/\d+/g, ' ').trim()
      const words = name.split(/\s+/).filter(Boolean)
      if (!words.length) return ''
      const first = words[0].charAt(0).toUpperCase() + words[0].slice(1)
      return first.length > 1 ? first : ''
    },
    dominantSubject() {
      const sc = (this.need && this.need.subcases && this.need.subcases[0] && this.need.subcases[0].keyword) || ''
      const kw = (this.need && this.need.top_keywords && this.need.top_keywords[0]) || ''
      const label = (this.need && this.need.label) || ''
      return (sc || kw || label || 'your project').trim().toLowerCase()
    },
    dominantUrgency() {
      const dist = (this.need && this.need.urgency_distribution) || {}
      const order = ['critical', 'high', 'medium', 'low']
      return order.find(k => (dist[k] || 0) > 0) || ''
    },
    recipientQuote(client) {
      if (!client) return ''
      const samples = (this.need && this.need.sample_intents) || []
      const own = samples.find(s => s.user_id === client.user_id)
      return own && own.text ? String(own.text).trim() : ''
    },
    getInitials(name) {
      if (!name) return '?'
      const parts = name.split(/[\s._-]+/).filter(Boolean)
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name.substring(0, 2).toUpperCase()
    },
    truncate(text, max) {
      if (!text) return ''
      return text.length > max ? text.substring(0, max) + '…' : text
    },
    formatAmount(amount) {
      if (!amount && amount !== 0) return ''
      return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA'
    },
    formatAmountShort(amount) {
      if (!amount && amount !== 0) return '0'
      if (amount >= 1000000) return (amount / 1000000).toFixed(1) + 'M'
      if (amount >= 1000) return (amount / 1000).toFixed(0) + 'K'
      return amount.toString()
    },
    formatDate(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      const pad = n => String(n).padStart(2, '0')
      return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear()
    }
  }
}
</script>

<style scoped>
/* ── Main dialog ── */
.need-dialog-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px);
  border-radius: 20px !important;
  overflow: hidden;
  border: 1px solid rgba(0, 150, 136, 0.2);
  color: #212121;
  display: flex;
  flex-direction: column;
}

.need-header {
  padding: 22px 20px 24px;
  position: relative;
  flex-shrink: 0;
}

.close-btn {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
}

.need-header-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-top: 6px;
}

.need-avatar {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.22);
  flex-shrink: 0;
}

.need-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.need-meta {
  font-size: 12.5px;
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.92);
}

.need-body {
  padding: 18px 20px 20px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.need-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}

.need-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(0, 150, 136, 0.06);
  border: 1px solid rgba(0, 150, 136, 0.12);
  border-radius: 12px;
  padding: 11px 6px;
}

.ns-value {
  font-size: 18px;
  font-weight: 800;
  color: #00897b;
}

.ns-label {
  font-size: 10.5px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 3px;
  text-align: center;
}

.need-block {
  margin-bottom: 18px;
}

.need-block-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.need-block-head.clickable {
  cursor: pointer;
  user-select: none;
}

.toggle-chevron {
  transition: transform 0.25s ease;
  color: #00897b;
}

.collapse-count {
  font-size: 11px;
  font-weight: 700;
  color: #00897b;
  background: rgba(0, 150, 136, 0.1);
  border: 1px solid rgba(0, 150, 136, 0.2);
  border-radius: 10px;
  padding: 3px 10px;
  white-space: nowrap;
}

.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.25s ease;
}

.collapse-enter,
.collapse-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.need-block-head .need-block-title {
  margin: 0;
}

.client-search {
  max-width: 250px;
  flex-shrink: 1;
}

.need-block-title {
  font-size: 12.5px;
  font-weight: 800;
  margin: 0 0 10px;
  color: #00897b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.tag-row,
.subcase-row {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.kw-chip {
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15));
  color: #4f63d6;
}

.urgency-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 10px;
}

.urgency-chip.ure-critical { background: rgba(239, 68, 68, 0.15); color: #dc2626; }
.urgency-chip.ure-high { background: rgba(251, 146, 60, 0.15); color: #ea580c; }
.urgency-chip.ure-medium { background: rgba(59, 130, 246, 0.15); color: #2563eb; }
.urgency-chip.ure-low { background: rgba(34, 197, 94, 0.15); color: #16a34a; }

.subcase-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(0, 150, 136, 0.2);
  border-radius: 12px;
}

.subcase-kw {
  font-size: 12.5px;
  font-weight: 700;
  color: #212121;
}

.subcase-count {
  font-size: 11px;
  font-weight: 700;
  color: #00897b;
  background: rgba(0, 150, 136, 0.1);
  padding: 2px 8px;
  border-radius: 8px;
}

.sample-item {
  display: flex;
  gap: 10px;
  align-items: baseline;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 10px;
  margin-bottom: 6px;
}

.sample-name {
  font-size: 12px;
  font-weight: 800;
  color: #00897b;
  min-width: 90px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sample-text {
  font-size: 12.5px;
  font-style: italic;
  color: rgba(0, 0, 0, 0.65);
}

.clients-list {
  max-height: 250px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.client-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  background: rgba(255, 255, 255, 0.65);
  border: 1px solid rgba(0, 150, 136, 0.12);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.client-row:hover {
  background: rgba(0, 150, 136, 0.07);
  border-color: rgba(0, 150, 136, 0.3);
}

.client-row.active {
  background: rgba(0, 150, 136, 0.12);
  border-color: #00897b;
  box-shadow: 0 2px 10px rgba(0, 150, 136, 0.15);
}

.client-avatar {
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 12.5px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.client-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.client-name {
  font-size: 13px;
  font-weight: 700;
  color: #212121;
}

.client-sub {
  font-size: 11.5px;
  color: rgba(0, 0, 0, 0.5);
}

.client-kw {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.mini-kw {
  font-size: 10.5px;
  font-weight: 600;
  color: #4f63d6;
  background: rgba(102, 126, 234, 0.1);
  padding: 2px 7px;
  border-radius: 8px;
}

.message-btn {
  flex-shrink: 0;
  text-transform: none;
  font-weight: 700;
  letter-spacing: 0;
  border-radius: 9px;
}

.list-empty {
  text-align: center;
  padding: 20px 10px;
  font-size: 12.5px;
  color: rgba(0, 0, 0, 0.4);
}

.products-strip {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 8px;
}

.sug-product {
  min-width: 150px;
  max-width: 170px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(0, 150, 136, 0.15);
  border-radius: 14px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sug-product:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
}

.sug-product.selected {
  border-color: #00897b;
  background: rgba(0, 150, 136, 0.09);
  box-shadow: 0 4px 14px rgba(0, 150, 136, 0.2);
}

.sug-add {
  border-style: dashed !important;
  border-color: rgba(0, 150, 136, 0.4) !important;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.sug-img {
  width: 100%;
  height: 82px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.12), rgba(0, 121, 107, 0.16));
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 8px;
}

.sug-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sug-info {
  display: flex;
  flex-direction: column;
}

.sug-name {
  font-size: 12.5px;
  font-weight: 700;
  color: #212121;
}

.sug-price {
  font-size: 12.5px;
  font-weight: 800;
  color: #00897b;
  margin-top: 2px;
}

.sug-cat {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 2px;
}

.product-attached {
  margin: 10px 0 0;
  font-size: 12px;
  color: #00897b;
  display: flex;
  align-items: center;
  gap: 5px;
}

.need-footer {
  padding: 12px 20px 18px;
  flex-shrink: 0;
}

.cta-btn {
  text-transform: none;
  font-weight: 700;
  letter-spacing: 0;
  border-radius: 12px;
}

.cta-target {
  margin-left: 8px;
  font-weight: 600;
  opacity: 0.85;
}

/* ── Chat popup ── */
.chat-popup-card {
  background: rgba(255, 255, 255, 0.98) !important;
  border-radius: 20px !important;
  overflow: hidden;
  color: #212121;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0, 150, 136, 0.2);
}

.chat-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  background: linear-gradient(135deg, #00897b 0%, #00695c 100%);
  flex-shrink: 0;
}

.chat-head-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chat-head-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-head-title {
  font-size: 17px;
  font-weight: 700;
  color: #fff;
}

.chat-head-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
}

.chat-body {
  padding: 16px 18px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.recipient-strip {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: rgba(0, 150, 136, 0.08);
  border: 1px solid rgba(0, 150, 136, 0.2);
  border-radius: 12px;
  margin-bottom: 14px;
}

.recipient-avatar {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.recipient-info {
  flex: 1;
  min-width: 0;
}

.recipient-name {
  font-size: 14px;
  font-weight: 700;
  color: #212121;
}

.recipient-sub {
  font-size: 11.5px;
  color: rgba(0, 0, 0, 0.5);
}

.switch-client-btn {
  flex-shrink: 0;
  border-radius: 9px;
}

.chat-templates {
  margin-bottom: 12px;
}

.chat-templates-label {
  font-size: 11.5px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 7px;
}

.chat-templates-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.preset-chip {
  font-size: 11.5px;
  font-weight: 700;
  color: #4f63d6;
  background: rgba(102, 126, 234, 0.12);
  border: 1px solid rgba(102, 126, 234, 0.25);
  padding: 4px 11px;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.preset-chip:hover {
  background: rgba(102, 126, 234, 0.22);
  transform: translateY(-1px);
}

.attached-product {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(0, 150, 136, 0.08);
  border: 1px solid rgba(0, 150, 136, 0.25);
  border-radius: 12px;
  margin-bottom: 12px;
}

.ap-img {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(0, 150, 136, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}

.ap-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ap-info {
  flex: 1;
  min-width: 0;
}

.ap-name {
  font-size: 12.5px;
  font-weight: 700;
  color: #212121;
}

.ap-price {
  font-size: 12px;
  font-weight: 700;
  color: #00897b;
}

.ap-attach {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #00897b;
  margin-top: 2px;
}

.form-group {
  margin-bottom: 12px;
}

.notify-channels {
  margin-bottom: 14px;
  padding: 12px 14px;
  background: rgba(0, 150, 136, 0.07);
  border: 1px solid rgba(0, 150, 136, 0.18);
  border-radius: 12px;
}

.notify-label {
  font-size: 11.5px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;
}

.notify-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.notify-chip {
  cursor: pointer;
  font-weight: 700 !important;
  color: rgba(0, 0, 0, 0.55) !important;
  background: rgba(255, 255, 255, 0.85) !important;
  border: 1px solid rgba(0, 0, 0, 0.15) !important;
  transition: all 0.2s ease;
}

.notify-chip.active {
  color: #00897b !important;
  background: rgba(0, 150, 136, 0.12) !important;
  border-color: #00897b !important;
}

.notify-hint {
  display: block;
  margin-top: 8px;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
}

.chat-label {
  font-size: 11.5px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 6px;
  display: block;
}

.chat-composer-input >>> .v-input__control {
  min-height: 40px;
}

.chat-textarea-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}

.file-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-weight: 600;
  background: rgba(0, 150, 136, 0.1);
  color: #00897b;
  padding: 4px 10px;
  border-radius: 10px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-remove {
  cursor: pointer;
  font-weight: 800;
  margin-left: 4px;
}

.char-count {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.4);
}

.char-count.char-warn {
  color: #ea580c;
  font-weight: 700;
}

.chat-preview {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 12px;
  padding: 12px;
}

.chat-preview-head {
  font-size: 11.5px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.chat-preview-attach {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid rgba(0, 150, 136, 0.25);
  border-radius: 10px;
  padding: 6px 10px;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  max-width: 100%;
}

.chat-preview-attach-thumb {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 150, 136, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-preview-attach-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.chat-preview-attach-name {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  font-weight: 700;
  color: #212121;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-preview-frame {
  background: #e3e8e0;
  border-radius: 14px 14px 14px 4px;
  padding: 12px;
  position: relative;
}

.chat-preview-bubble {
  background: linear-gradient(135deg, #00897b, #00695c);
  color: #fff;
  padding: 10px 14px;
  border-radius: 14px 14px 14px 4px;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.chat-preview-tick {
  display: inline-block;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.4);
  margin-top: 8px;
}

.chat-foot {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 12px 18px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}

.phost-btn {
  text-transform: none;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.6);
}

.send-btn {
  border-radius: 10px;
  text-transform: none;
  font-weight: 700;
  letter-spacing: 0;
}

/* ── Product picker ── */
.product-popup-card {
  background: rgba(255, 255, 255, 0.98) !important;
  border-radius: 20px !important;
  overflow: hidden;
  color: #212121;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0, 150, 136, 0.2);
}

.product-popup-head {
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
}

.product-popup-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pp-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.pp-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
}

.pp-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.92);
}

.product-popup-body {
  padding: 16px 18px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.pp-controls {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.pp-search {
  flex: 1;
}

.pp-price {
  max-width: 170px;
}

.pp-cats {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 4px;
}

.pp-cat-chip {
  font-size: 11.5px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.6);
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 4px 12px;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pp-cat-chip.active {
  background: rgba(0, 150, 136, 0.12);
  border-color: #00897b;
  color: #00897b;
}

.pp-divider {
  margin: 12px 0;
}

.pp-loading,
.pp-empty {
  text-align: center;
  padding: 30px 10px;
  color: rgba(0, 0, 0, 0.4);
}

.pp-empty p {
  margin: 8px 0 0;
  font-size: 13px;
}

.pp-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}

.pp-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(0, 150, 136, 0.15);
  border-radius: 14px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
}

.pp-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
}

.pp-item.attached {
  border-color: #00897b;
  background: rgba(0, 150, 136, 0.09);
  box-shadow: 0 4px 14px rgba(0, 150, 136, 0.2);
}

.pp-item-img {
  width: 100%;
  height: 96px;
  border-radius: 10px;
  background: rgba(0, 150, 136, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.pp-item-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pp-item-info {
  display: flex;
  flex-direction: column;
}

.pp-item-name {
  font-size: 12.5px;
  font-weight: 700;
  color: #212121;
}

.pp-item-cat {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 2px;
}

.in-stock { color: #16a34a; font-weight: 600; }
.out-stock { color: #dc2626; font-weight: 600; }

.pp-item-price {
  font-size: 12.5px;
  font-weight: 800;
  color: #00897b;
  margin-top: 2px;
}

.product-popup-foot {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 12px 18px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}

.pp-foot-count {
  flex: 1;
  font-size: 12px;
  font-weight: 700;
  color: #00897b;
}

/* ── Multi-select (clients / products) ── */
.need-block-head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.client-check {
  flex-shrink: 0;
}

.client-check .v-icon {
  transition: all 0.2s ease;
}

.clients-list .client-row.active .client-check .v-icon {
  animation: popIn 0.2s ease;
}

.sug-check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 2;
}

.sug-check .v-icon {
  color: #fff;
}

.sug-check.on {
  background: #00897b;
}

.sug-product {
  position: relative;
}

.recipients-area {
  margin-bottom: 12px;
}

.recipients-label {
  font-size: 11.5px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
}

.recipients-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.recipient-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 150, 136, 0.35);
  border-radius: 20px;
  padding: 3px 8px 3px 4px;
  font-size: 12px;
  font-weight: 700;
  color: #212121;
  transition: all 0.2s ease;
}

.rc-avatar {
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
}

.rc-name {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rc-remove {
  cursor: pointer;
  font-weight: 800;
  color: rgba(0, 0, 0, 0.4);
  font-size: 14px;
  line-height: 1;
  padding: 0 2px;
}

.rc-remove:hover {
  color: #dc2626;
}

.add-recipient-btn,
.add-product-btn {
  border-radius: 20px;
  text-transform: none;
  font-weight: 700;
  letter-spacing: 0;
}

.attached-products {
  margin-bottom: 12px;
}

.attached-label {
  font-size: 11.5px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
}

.ap-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.ap-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 150, 136, 0.08);
  border: 1px solid rgba(0, 150, 136, 0.3);
  border-radius: 20px;
  padding: 3px 8px 3px 4px;
  font-size: 12px;
  color: #212121;
}

.ap-chip img {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  object-fit: cover;
  background: rgba(0, 150, 136, 0.15);
}

.ap-chip-name {
  font-weight: 700;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ap-chip-price {
  font-weight: 800;
  color: #00897b;
  font-size: 11.5px;
}

.ap-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #00897b;
}

.ap-empty {
  display: flex;
}

@keyframes popIn {
  0% { transform: scale(0.6); }
  70% { transform: scale(1.15); }
  100% { transform: scale(1); }
}

/* ── Mobile ── */
@media (max-width: 600px) {
  .need-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .need-header {
    padding: 18px 16px 20px;
  }

  .need-body {
    padding: 14px 14px 16px;
    max-height: calc(100vh - 210px);
  }

  .client-search {
    max-width: 100%;
  }

  .chat-body,
  .product-popup-body {
    padding: 14px;
    max-height: calc(100vh - 210px);
  }

  .pp-controls {
    flex-direction: column;
  }

  .pp-price {
    max-width: 100%;
  }

  .pp-list {
    grid-template-columns: repeat(2, 1fr);
  }

  .msg-bubble,
  .chat-preview-bubble {
    max-width: 100%;
  }

  .need-footer {
    padding: 10px 14px 14px;
  }
}
</style>